import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref, watch } from "vue";
import { useTranslations } from "@/hooks/useTranslations";
import type { SelectedActivityInput } from "@/models/wellness/SelectedActivityInput";
import type { WellnessCalculationRequest } from "@/models/wellness/WellnessCalculationRequest";
import type { WellnessCalculationResult } from "@/models/wellness/WellnessCalculationResult";
import type { WellnessItem } from "@/models/wellness/WellnessItem";
import menuSanoLogo from "@/assets/img/logo-apple.png";
import { calculateWellnessActivities } from "../../services/wellnessApi";
import EmptyState from "./EmptyState.vue";
import UserInputCard from "./UserInputCard.vue";
import ResultsPanel from "./ResultsPanel.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'CaloriePlanner',
  props: {
    selectedItem: null
  },
  emits: ["export-availability-change"],
  setup(__props: any, { expose, emit }: { emit: ({
  (event: "export-availability-change", value: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { selectedItem: WellnessItem | null };




const vm = getCurrentInstance();
const translations = useTranslations();
const resultsExportRoot = ref<HTMLElement | null>(null);
const results = ref<WellnessCalculationResult[]>([]);
const loading = ref(false);
const exportingPdf = ref(false);
const html2PdfLoader = ref<Promise<any> | null>(null);
const lastCalculationInput = ref<{
  mode?: WellnessCalculationRequest["mode"];
  sex: WellnessCalculationRequest["sex"];
  heightCm: number;
  weightKg: number;
  age: number;
  activities: SelectedActivityInput[];
  selectedActivityTypeIds?: number[];
} | null>(null);
const isCaloriesReady = computed(
  () =>
    props.selectedItem?.calories !== undefined &&
    props.selectedItem?.calories !== null,
);
const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});
const formattedCalories = computed(() =>
  numberFormatter.format(props.selectedItem?.calories || 0),
);

function sanitizeDocumentTitlePart(value: string) {
  return value
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildExportDocumentTitle() {
  const itemName = sanitizeDocumentTitlePart(props.selectedItem?.name || "Wellness Analyzer");
  const calculation = lastCalculationInput.value;
  const genderLabel = calculation
    ? calculation.sex === "male"
      ? translations.value.get("wellness_print_male")
      : translations.value.get("wellness_print_female")
    : "";
  const activityTypes = Array.from(
    new Set(
      results.value
        .map((result) => sanitizeDocumentTitlePart(result.activityTypeName || ""))
        .filter(Boolean),
    ),
  );
  const titleParts = [itemName];

  if (genderLabel) {
    titleParts.push(sanitizeDocumentTitlePart(genderLabel));
  }

  if (activityTypes.length > 0) {
    titleParts.push(activityTypes.join(", "));
  }

  titleParts.push("MenuSano");

  return titleParts.join(" - ");
}

function buildExportFilename() {
  return `${buildExportDocumentTitle()}.pdf`;
}

watch(
  () => props.selectedItem,
  () => {
    results.value = [];
    lastCalculationInput.value = null;
  },
);

watch(
  [results, exportingPdf, () => props.selectedItem],
  () => {
    emit("export-availability-change", canExportPdf());
  },
  { immediate: true },
);

function buildExportMarkup() {
  if (!props.selectedItem || results.value.length === 0 || !resultsExportRoot.value) {
    return "";
  }

  const documentTitle = buildExportDocumentTitle();
  const calculation = lastCalculationInput.value;
  const reportDate = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date());
  const styles = Array.from(document.querySelectorAll("style, link[rel='stylesheet']"))
    .map((node) => node.outerHTML)
    .join("\n");
  const exportRoot = resultsExportRoot.value.cloneNode(true) as HTMLElement;
  if (calculation) {
    results.value.forEach((result) => {
      const heading = exportRoot.querySelector(
        `[data-testid="wellness-result-${result.activityId}"] .result-activity-name`,
      );
      const selectedActivity = calculation.activities.find(
        (activity) => activity.activityId === result.activityId,
      );

      if (heading && selectedActivity?.enteredSpeedMph) {
        heading.textContent = `${result.activityName} - ${selectedActivity.enteredSpeedMph} mph`;
      }
    });
  }
  const exportContent = exportRoot.innerHTML;
  const summaryMarkup = calculation
    ? `
      <section class="print-summary-grid">
        <div class="print-summary-card">
          <span>${translations.value.get("wellness_print_gender")}</span>
          <strong>${calculation.sex === "male" ? translations.value.get("wellness_print_male") : translations.value.get("wellness_print_female")}</strong>
        </div>
        <div class="print-summary-card">
          <span>${translations.value.get("wellness_print_age")}</span>
          <strong>${calculation.age}</strong>
        </div>
        <div class="print-summary-card">
          <span>${translations.value.get("wellness_print_height")}</span>
          <strong>${Math.round(calculation.heightCm)} cm</strong>
        </div>
        <div class="print-summary-card">
          <span>${translations.value.get("wellness_print_weight")}</span>
          <strong>${calculation.weightKg.toFixed(1)} kg</strong>
        </div>
      </section>
    `
    : "";

  return `
    <!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=1440, initial-scale=1" />
        <title>${documentTitle}</title>
        ${styles}
        <style>
          html,
          body {
            width: 100%;
            min-width: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body {
            margin: 0;
            padding: 10px;
            background: #ffffff;
            font-family: Arial, sans-serif;
            overflow-x: hidden;
          }
          .print-shell-fit {
            width: 740px;
            max-width: 740px;
            margin: 0 auto;
            overflow: hidden;
          }
          .print-shell {
            width: 920px;
            max-width: 920px;
            margin: 0 auto;
            transform: scale(0.8);
            transform-origin: top left;
          }
          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 10px;
            margin-bottom: 24px;
          }
          .print-header-logo {
            flex: 0 0 auto;
            display: flex;
            align-items: flex-start;
            justify-content: flex-end;
          }
          .print-header-logo img {
            display: block;
            width: 92px;
            height: auto;
            object-fit: contain;
          }
          .print-header h1 {
            margin: 0 0 4px;
            color: #33691e;
            font-size: 28px;
          }
          .print-header p {
            margin: 0;
            color: #6b7280;
            font-size: 13px;
          }
          .print-summary-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 10px;
            margin-bottom: 18px;
          }
          .print-summary-card {
            padding: 12px 14px;
            border: 1px solid #d6e0ea;
            border-radius: 14px;
            background: #ffffff;
          }
          .print-summary-card span {
            display: block;
            margin-bottom: 4px;
            color: #111827;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.04em;
            text-transform: uppercase;
          }
          .print-summary-card strong {
            color: #111827;
            font-size: 18px;
            font-weight: 700;
          }
          .print-shell .d-flex.flex-column.gap-3 {
            gap: 1rem !important;
          }
          .print-shell .result-card {
            position: relative;
            border-radius: 18px;
            border: 1px solid #d6e0ea;
            background: #ffffff;
            padding: 1.1rem 1.05rem;
            page-break-inside: avoid;
            break-inside: avoid;
            overflow: hidden;
          }
          .print-shell .result-group-header {
            display: flex !important;
            flex-direction: row !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 0.75rem !important;
          }
          .print-shell .result-group-count {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            white-space: nowrap !important;
            font-size: 0.9rem;
          }
          .print-shell .result-header {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.2rem;
          }
          .print-shell .result-activity-name {
            width: 100%;
            text-align: center;
            font-size: 1.15rem;
            font-weight: 700;
            color: #1f2d3d;
          }
          .print-shell .result-graph-layout {
            display: grid !important;
            grid-template-columns: 116px minmax(0, 1fr) 116px !important;
            align-items: center !important;
            gap: 0.85rem !important;
          }
          .print-shell .result-radial-card {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .print-shell .result-target-block {
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 0.75rem !important;
            min-height: 98px;
            padding: 0.9rem 1rem;
            border: 1px solid #d6e0ea;
            border-radius: 14px;
            background: #ffffff;
            min-width: 0;
          }
          .print-shell .result-value {
            font-size: 1rem;
            line-height: 1.35;
          }
          .print-shell .result-meta {
            font-size: 0.85rem;
            line-height: 1.4;
          }
          .print-shell .activity-illustration {
            flex: 0 0 60px !important;
            width: 56px !important;
            height: 56px !important;
            border-radius: 14px;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            background: #dcedff;
          }
          .print-shell .comparison2-wrap {
            padding: 0.7rem 0.55rem 0.45rem;
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            background: #fcfdff;
            page-break-inside: avoid;
            break-inside: avoid;
            overflow: visible;
          }
          .print-shell .comparison2-row {
            display: grid !important;
            grid-template-columns: 128px minmax(0, 1fr) 40px !important;
            align-items: center !important;
            gap: 0.55rem !important;
            margin-bottom: 0.7rem;
          }
          .print-shell .comparison2-bar-zone {
            position: relative;
            height: 22px;
            border-radius: 8px;
            background: #e9eef4 !important;
            overflow: visible;
          }
          .print-shell .comparison2-bar {
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            min-width: 6px;
            border-radius: 8px;
            background: #aab5c7 !important;
          }
          .print-shell .comparison2-bar.fastest {
            background: #33691e !important;
          }
          .print-shell .comparison2-ylabel,
          .print-shell .comparison2-value,
          .print-shell .comparison2-xaxis,
          .print-shell .tooltip-title,
          .print-shell .tooltip-value {
            font-size: 0.8rem;
          }
          .print-shell .radial-main {
            font-size: 18px;
            font-weight: 700;
          }
          .print-shell .radial-sub {
            font-size: 11px;
          }
          .print-shell .radial-main-lg {
            font-size: 16px;
          }
          .print-shell .radial-main-xl {
            font-size: 13px;
          }
          .print-shell .result-radial-card svg {
            width: 108px;
            height: 108px;
          }
          .print-shell .result-radial-card.right svg {
            width: 108px;
            height: 108px;
          }
          .print-shell .result-radial-card.right .radial-main {
            font-size: 15px;
          }
          .print-shell .result-radial-card.right .radial-main-lg {
            font-size: 13px;
          }
          .print-shell .result-radial-card.right .radial-main-xl {
            font-size: 12px;
          }
          .print-shell .result-radial-card.right .radial-sub {
            font-size: 9px;
          }
          .print-shell .comparison2-axis-row {
            display: grid !important;
            grid-template-columns: 128px minmax(0, 1fr) 40px !important;
            align-items: start !important;
            gap: 0.55rem !important;
            margin-top: 0.65rem;
          }
          .print-shell .comparison2-xaxis {
            display: grid !important;
            grid-template-columns: repeat(6, 1fr) !important;
          }
          .print-shell .comparison2-grid-lines {
            display: grid !important;
            grid-template-columns: repeat(6, 1fr) !important;
          }
          @page {
            size: letter portrait;
            margin: 10mm;
          }
          @media print {
            body {
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <main class="print-shell-fit">
          <div class="print-shell">
            <header class="print-header">
              <div>
                <h1>${translations.value.get("wellness_print_header")}</h1>
                <p>${reportDate}</p>
              </div>
              <div class="print-header-logo">
                <img src="${menuSanoLogo}" alt="MenuSano Logo" />
              </div>
            </header>
            ${summaryMarkup}
            ${exportContent}
          </div>
        </main>
      </body>
    </html>
  `;
}

function buildExportContainer(markup: string) {
  const parser = new DOMParser();
  const exportDocument = parser.parseFromString(markup, "text/html");
  const container = document.createElement("div");

  container.style.position = "fixed";
  container.style.left = "-10000px";
  container.style.top = "0";
  container.style.width = "760px";
  container.style.background = "#ffffff";
  container.style.zIndex = "-1";

  Array.from(exportDocument.head.children).forEach((node) => {
    container.appendChild(node.cloneNode(true));
  });

  Array.from(exportDocument.body.children).forEach((node) => {
    container.appendChild(node.cloneNode(true));
  });

  return container;
}

function loadHtml2Pdf() {
  const existingHtml2Pdf = (window as any).html2pdf;
  if (existingHtml2Pdf) {
    return Promise.resolve(existingHtml2Pdf);
  }

  if (html2PdfLoader.value) {
    return html2PdfLoader.value;
  }

  html2PdfLoader.value = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      'script[data-html2pdf-script="true"]',
    ) as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve((window as any).html2pdf), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Failed to load html2pdf")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = `${process.env.BASE_URL || "/"}vendor/html2pdf.bundle.min.js`;
    script.async = true;
    script.dataset.html2pdfScript = "true";
    script.onload = () => resolve((window as any).html2pdf);
    script.onerror = () => reject(new Error("Failed to load html2pdf"));
    document.head.appendChild(script);
  });

  return html2PdfLoader.value;
}

async function handleExportPdf() {
  if (!props.selectedItem || results.value.length === 0) {
    return;
  }

  const exportMarkup = buildExportMarkup();
  if (!exportMarkup) {
    vm?.proxy?.$snotify?.error(translations.value.get("wellness_export_no_results"));
    return;
  }

  const exportFilename = buildExportFilename();
  exportingPdf.value = true;
  const exportContainer = buildExportContainer(exportMarkup);
  try {
    const html2pdf = await loadHtml2Pdf();
    document.body.appendChild(exportContainer);
    const exportTarget = exportContainer.querySelector(".print-shell-fit") as HTMLElement | null;

    if (!exportTarget) {
      throw new Error("Unable to prepare export target");
    }

    await html2pdf()
      .set({
        margin: [8, 8, 8, 8],
        filename: exportFilename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
        },
        jsPDF: {
          unit: "mm",
          format: "letter",
          orientation: "portrait",
        },
        pagebreak: {
          mode: ["css", "legacy"],
        },
      })
      .from(exportTarget)
      .save();
  } catch (error) {
    vm?.proxy?.$snotify?.error(translations.value.get("wellness_export_prepare_failed"));
  } finally {
    exportContainer.remove();
    exportingPdf.value = false;
  }
}

function canExportPdf() {
  return Boolean(props.selectedItem && results.value.length > 0 && !exportingPdf.value);
}

expose({
  exportToPdf: handleExportPdf,
  canExportPdf,
});

async function handleCalculate(payload: {
  mode?: WellnessCalculationRequest["mode"];
  sex: WellnessCalculationRequest["sex"];
  heightCm: number;
  weightKg: number;
  activities: SelectedActivityInput[];
  age: number;
  selectedActivityTypeIds?: number[];
}) {
  if (!props.selectedItem?.calories) {
    return;
  }

  lastCalculationInput.value = {
    mode: payload.mode,
    sex: payload.sex,
    heightCm: payload.heightCm,
    weightKg: payload.weightKg,
    age: payload.age,
    activities: payload.activities,
    selectedActivityTypeIds: payload.selectedActivityTypeIds,
  };
  loading.value = true;
  try {
    results.value = await calculateWellnessActivities({
      mode: payload.mode,
      sex: payload.sex,
      heightCm: payload.heightCm,
      weightKg: payload.weightKg,
      age: payload.age,
      caloriesTarget: props.selectedItem.calories,
      selectedActivityTypeIds: payload.selectedActivityTypeIds,
      activities: payload.activities,
    });
  } catch (error: any) {
    const message =
      error?.response?.data?.errorMessages?.[0]?.userVisibleMessage ||
      error?.response?.data ||
      "Network error";
    vm?.proxy?.$snotify?.error(message);
    results.value = [];
  } finally {
    loading.value = false;
  }
}

return { __sfc: true,props, emit, vm, translations, resultsExportRoot, results, loading, exportingPdf, html2PdfLoader, lastCalculationInput, isCaloriesReady, numberFormatter, formattedCalories, sanitizeDocumentTitlePart, buildExportDocumentTitle, buildExportFilename, buildExportMarkup, buildExportContainer, loadHtml2Pdf, handleExportPdf, canExportPdf, handleCalculate, EmptyState, UserInputCard, ResultsPanel }
}

})